"use client";

import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FolderOpen, Trash2, GripVertical, CheckCircle, Clock, AlertCircle, Video } from "lucide-react";
import { Group } from "@/types/group.interface";

interface DraggableGroupItemProps {
    group: Group;
    isActive: boolean;
    onSelect: (id: string) => void;
    onDelete: (id: string, e: React.MouseEvent) => void;
    onViewResult?: (group: Group, e: React.MouseEvent) => void;
}

export function DraggableGroupItem({
    group,
    isActive,
    onSelect,
    onDelete,
    onViewResult,
}: DraggableGroupItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: group.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 1,
        opacity: isDragging ? 0.5 : 1,
    };

    const getStatusIcon = () => {
        switch (group.status) {
            case "completed":
                return <CheckCircle size={12} className="text-green-500" />;
            case "error":
                return <AlertCircle size={12} className="text-red-500" />;
            default:
                return <Clock size={12} className="text-blue-500" />;
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`group flex items-center gap-2 p-2 border transition-all cursor-pointer rounded-none mb-1
                ${isActive
                    ? 'bg-blue-50 dark:bg-blue-600/10 border-blue-200 dark:border-blue-600/30'
                    : 'bg-white dark:bg-zinc-900 border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800 hover:border-zinc-200 dark:hover:border-zinc-700'
                }`}
            onClick={() => onSelect(group.id)}
        >
            <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing text-zinc-600 hover:text-zinc-400 p-0.5"
            >
                <GripVertical size={14} />
            </div>

            <div className="shrink-0 flex items-center gap-1.5">
                <FolderOpen size={16} />
                {getStatusIcon()}
            </div>

            <span className="flex-1 truncate text-sm font-medium">
                {group.name}
            </span>

            {group.status === "completed" && group.finalVideoUrl && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onViewResult?.(group, e);
                    }}
                    className="p-1.5 rounded-none text-emerald-500 hover:bg-emerald-500/10 transition-all"
                    title="查看成品"
                >
                    <Video size={14} fill="currentColor" />
                </button>
            )}

            <button
                onClick={(e) => onDelete(group.id, e)}
                className={`p-1.5 rounded-none opacity-0 group-hover:opacity-100 hover:bg-red-500/20 hover:text-red-400 transition-all ${isActive ? "text-blue-400/50" : "text-zinc-600"
                    }`}
            >
                <Trash2 size={14} />
            </button>
        </div>
    );
}
